import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Loader2, Search, SearchX, RefreshCw } from "lucide-react";

import { orpc } from "@/client";
import type { ConversationListPage } from "@/types/conversation";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Conversations } from "@/lib/conversation-session";
import { format } from "@/lib/format";
import { cn } from "@/lib/utils";

export function ConversationsList({ selected }: { selected: number | undefined }) {
	const [search, setSearch] = useState(Conversations.list.search);
	const [query, setQuery] = useState(search);
	const [waiting, setWaiting] = useState(Conversations.list.waiting);
	const listRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const timer = setTimeout(() => {
			if (query !== search.trim()) {
				Conversations.list.top = 0;
				listRef.current?.scrollTo({ top: 0 });
			}

			setQuery(search.trim());
		}, 300);

		return () => clearTimeout(timer);
	}, [search, query]);

	const { data, isPending, isError, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
		useInfiniteQuery(
			orpc.messages.list.infiniteOptions({
				input: (before: ConversationListPage["next_before"]) => ({
					search: query,
					waiting,
					before,
				}),
				initialPageParam: null as ConversationListPage["next_before"],
				getNextPageParam: (page) => page.next_before,
				gcTime: 10 * 60 * 1000,
				staleTime: Infinity,
				refetchOnWindowFocus: false,
				refetchOnReconnect: false,
			}),
		);

	useLayoutEffect(() => {
		if (listRef.current && listRef.current.clientHeight > 0) {
			listRef.current.scrollTop = Conversations.list.top;
		}
	}, [selected, !!data]);

	return (
		<section
			aria-label="Lista de conversas"
			className="flex min-h-0 w-full flex-col border-r border-border"
		>
			<div className="flex flex-col gap-3 border-b border-border p-4">
				<label className="flex items-center gap-2 rounded-lg border border-border bg-background px-3">
					<Search className="size-4 shrink-0 text-muted-foreground" />
					<input
						aria-label="Buscar por nome ou telefone"
						placeholder="Nome ou telefone"
						value={search}
						onChange={(event) => {
							setSearch(event.target.value);
							Conversations.list.search = event.target.value;
						}}
						className="h-10 min-w-0 flex-1 bg-transparent text-sm focus-visible:outline-none"
					/>
				</label>
				<div className="flex gap-2">
					{[
						{ value: false, label: "Todas" },
						{ value: true, label: "Aguardando resposta" },
					].map((filter) => (
						<button
							key={filter.label}
							type="button"
							aria-pressed={waiting === filter.value}
							onClick={() => {
								setWaiting(filter.value);
								Conversations.list.waiting = filter.value;
								Conversations.list.top = 0;
								listRef.current?.scrollTo({ top: 0 });
							}}
							className={cn(
								"rounded-full px-2.5 py-1.5 text-xs focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
								waiting === filter.value
									? "bg-primary-soft font-medium text-primary"
									: "text-muted-foreground hover:bg-background",
							)}
						>
							{filter.label}
						</button>
					))}
				</div>
			</div>
			<div
				ref={listRef}
				onScroll={(event) => {
					if (event.currentTarget.clientHeight > 0) {
						Conversations.list.top = event.currentTarget.scrollTop;
					}
				}}
				className="min-h-0 flex-1 overflow-y-auto"
			>
				{isPending && (
					<div className="animate-pulse space-y-4 p-4">
						{Array.from({ length: 8 }, (_, i) => (
							<div key={i} className="h-16 rounded-lg bg-background" />
						))}
					</div>
				)}
				{isError && (
					<div role="alert" className="space-y-3 p-4 text-sm text-destructive">
						<p>Não foi possível atualizar as conversas.</p>
						<Button size="sm" variant="outline" onClick={() => refetch()}>
							<RefreshCw />
							Tentar de novo
						</Button>
					</div>
				)}
				{data?.pages.map((page) =>
					page.items.map((item) => (
						<Link
							key={item.person_id}
							to="/conversas"
							search={{ person: item.person_id }}
							aria-current={selected === item.person_id ? "page" : undefined}
							className={cn(
								"flex gap-3 border-b border-border/60 px-4 py-4 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-ring",
								selected === item.person_id ? "bg-primary-soft" : "hover:bg-background",
							)}
						>
							<span className="grid size-10 shrink-0 place-items-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
								{(item.name || item.phone || "?").slice(0, 1).toUpperCase()}
							</span>
							<div className="min-w-0 flex-1">
								<div className="flex items-baseline justify-between gap-2">
									<span className="truncate text-sm font-semibold text-foreground">
										{format.readableName(item.name) || item.phone || "Sem nome"}
									</span>
									<time className="shrink-0 text-xs text-muted-foreground">
										{format.time(item.last_message.sent_at)}
									</time>
								</div>
								<p className="mt-1 line-clamp-2 break-words text-xs leading-relaxed text-muted-foreground">
									{item.last_message.direction === "clinic" && <span>Você: </span>}
									{item.last_message.text}
								</p>
								{item.waiting && (
									<p className="mt-1.5 text-xs font-medium text-primary">Aguardando resposta</p>
								)}
							</div>
						</Link>
					)),
				)}
				{data?.pages[0]?.items.length === 0 && (
					<div className="p-4">
						<EmptyState icon={SearchX} title="Nenhuma conversa encontrada" />
					</div>
				)}
				{hasNextPage && (
					<div className="p-3">
						<Button
							className="w-full"
							variant="ghost"
							size="sm"
							disabled={isFetchingNextPage}
							onClick={() => fetchNextPage()}
						>
							{isFetchingNextPage && <Loader2 className="animate-spin" />}Mais conversas
						</Button>
					</div>
				)}
			</div>
		</section>
	);
}
